import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, downloadSessionAttendance } from "@/lib/api";
import { Check, X, Search, Hash, MapPin, Download } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { SessionsListSkeleton } from "@/components/skeletons/PageSkeletons";

function pctColor(pct) {
  if (pct >= 90) return "text-[#059669]";
  if (pct >= 75) return "text-[#C8102E]";
  if (pct >= 60) return "text-[#D97706]";
  return "text-[#E11D48]";
}

const rangeOptions = [
  { key: "today", label: "Today" },
  { key: "7", label: "Last 7 days" },
  { key: "30", label: "Last 30 days" },
  { key: "all", label: "All time" },
];

const sourceChip = {
  manual:   "bg-[#FDECEF] text-[#C8102E]",
  external: "bg-[#FEF3C7] text-[#B45309]",
  seed:     "bg-[#F1F5F9] text-[#475569]",
};

export default function Sessions() {
  const [sessions, setSessions] = useState([]);
  const [sections, setSections] = useState([]);
  const [sectionId, setSectionId] = useState("all");
  const [range, setRange] = useState("today");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const navigate = useNavigate();

  const params = useMemo(() => {
    const p = {};
    if (sectionId !== "all") p.section_id = sectionId;
    const today = new Date().toISOString().slice(0, 10);
    if (range === "today") {
      p.date_from = today;
      p.date_to = today;
    } else if (range !== "all") {
      const d = new Date();
      d.setDate(d.getDate() - (Number(range) - 1));
      p.date_from = d.toISOString().slice(0, 10);
    }
    return p;
  }, [sectionId, range]);

  useEffect(() => {
    setLoading(true);
    api.get("/sessions", { params }).then(r => setSessions(r.data)).finally(() => setLoading(false));
  }, [params]);

  useEffect(() => {
    api.get("/sections").then(r => setSections(r.data));
  }, []);

  const downloadSession = async (sessionId, e) => {
    e.stopPropagation();
    setDownloadingId(sessionId);
    try {
      await downloadSessionAttendance(sessionId);
      toast.success("Attendance downloaded");
    } catch (err) {
      toast.error(err.message || "Download failed");
    } finally {
      setDownloadingId(null);
    }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return sessions;
    return sessions.filter(x =>
      (x.course_name || "").toLowerCase().includes(s) ||
      (x.course_code || "").toLowerCase().includes(s) ||
      (x.section_name || "").toLowerCase().includes(s) ||
      (x.classroom_name || "").toLowerCase().includes(s) ||
      x.date.includes(s)
    );
  }, [sessions, q]);

  return (
    <div className="min-h-screen">
      <div className="px-6 md:px-12 pt-10 pb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">Class sessions log</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0F172A] mt-2" data-testid="sessions-heading">Sessions.</h1>
        <p className="text-[#475569] mt-3 max-w-2xl">Every class session — live roll-calls, historical data and API-ingested records — with who attended. Tap any row to see the students who were present or absent.</p>
      </div>

      {/* Filters */}
      <div className="px-6 md:px-12 pb-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap" data-testid="sessions-section-filters">
          <button
            onClick={() => setSectionId("all")}
            data-testid="sessions-filter-all"
            className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors duration-200 ease-in-out ${
              sectionId === "all" ? "bg-[#C8102E] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"
            }`}
          >
            All sections
          </button>
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => setSectionId(s.id)}
              data-testid={`sessions-filter-${s.id}`}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors duration-200 ease-in-out ${
                sectionId === s.id ? "bg-[#C8102E] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
        <div className="mx-2 h-6 w-px bg-[#E2E8F0]" />
        <div className="flex items-center gap-2" data-testid="sessions-range-filters">
          {rangeOptions.map(o => (
            <button
              key={o.key}
              onClick={() => setRange(o.key)}
              data-testid={`sessions-range-${o.key}`}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors duration-200 ease-in-out ${
                range === o.key ? "bg-[#0F172A] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-6 md:px-12 pb-4">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input
            placeholder="Search by course, section, room or date"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="sessions-search"
            className="pl-9 h-11 border-[#E2E8F0] focus:ring-2 focus:ring-red-600 focus:ring-offset-2"
          />
        </div>
      </div>

      <div className="px-6 md:px-12 pb-16">
        {loading ? (
          <SessionsListSkeleton />
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-[#94A3B8]" data-testid="sessions-empty">No sessions match this filter.</div>
        ) : (
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="sessions-table">
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC] text-[10px] font-bold uppercase tracking-[0.2em] text-[#94A3B8]">
              <div className="col-span-2">Date</div>
              <div className="col-span-4">Class</div>
              <div className="col-span-2">Section · Room</div>
              <div className="col-span-2">Breakdown</div>
              <div className="col-span-2 text-right">Attendance</div>
            </div>
            {filtered.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`/sessions/${s.id}`)}
                className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-4 items-center px-6 py-4 border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F1F5F9] transition-colors duration-200 ease-in-out"
                data-testid={`session-row-${s.id}`}
              >
                <div className="md:col-span-2">
                  <div className="font-semibold text-[#0F172A]">{s.date}</div>
                  {s.start_time && <div className="text-xs text-[#94A3B8]">{s.start_time}–{s.end_time}</div>}
                  <div className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded mt-1 ${sourceChip[s.source] || sourceChip.manual}`}>
                    {s.source}
                  </div>
                </div>
                <div className="md:col-span-4 min-w-0">
                  <div className="font-heading font-bold text-[#0F172A] truncate">{s.course_name}</div>
                  <div className="text-xs text-[#94A3B8] flex items-center gap-2 mt-0.5">
                    <span className="font-bold uppercase tracking-[0.15em]">{s.course_code}</span>
                    <span className="inline-flex items-center gap-1"><Hash className="w-3 h-3" />{String(s.id).slice(0, 8)}</span>
                  </div>
                </div>
                <div className="md:col-span-2 text-sm text-[#475569]">
                  <div className="font-semibold text-[#0F172A]">Section {s.section_name}</div>
                  {s.classroom_name && <div className="text-xs flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{s.classroom_name}</div>}
                </div>
                <div className="md:col-span-2 flex items-center gap-2 text-xs font-semibold">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#D1FAE5] text-[#059669]" data-testid={`row-present-${s.id}`}><Check className="w-3 h-3" />{s.present}</span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#FFE4E6] text-[#E11D48]" data-testid={`row-absent-${s.id}`}><X className="w-3 h-3" />{s.absent}</span>
                </div>
                <div className="md:col-span-2 flex flex-col md:items-end gap-1">
                  <div className={`font-heading text-lg font-extrabold tracking-tighter ${pctColor(s.attendance_pct)}`}>{s.attendance_pct}%</div>
                  <div className="text-xs text-[#94A3B8]">{s.present}/{s.total} attended</div>
                  <button
                    type="button"
                    onClick={(e) => downloadSession(s.id, e)}
                    disabled={downloadingId === s.id}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#475569] hover:text-[#0F172A] mt-1"
                    data-testid={`download-session-${s.id}`}
                  >
                    <Download className="w-3.5 h-3.5" />
                    {downloadingId === s.id ? "Downloading…" : "Excel"}
                  </button>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
